import React from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';
import { API_URL } from '../config';

// Centralized helper to get auth headers - use this everywhere for consistency
export const getAuthHeaders = (existingHeaders = {}) => {
  const headers = { ...existingHeaders };
  try {
    const authToken = localStorage.getItem('auth_token');
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }
  } catch (e) {
    // localStorage might not be available
  }
  return headers;
};

// Configured axios instance that automatically includes Authorization header
export const axiosWithAuth = axios.create({
  withCredentials: true,
});

// Add request interceptor to automatically include Authorization header
axiosWithAuth.interceptors.request.use(
  (config) => {
    const authToken = localStorage.getItem('auth_token');
    if (authToken) {
      config.headers = config.headers || {};
      config.headers['Authorization'] = `Bearer ${authToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Create a wrapper for fetch that automatically includes credentials and handles cookie issues
export const fetchWithAuth = async (endpoint, options = {}, errorMessage = '') => {
  const url = endpoint.startsWith('http') ? endpoint : `${API_URL}${endpoint.startsWith('/') ? endpoint : '/' + endpoint}`;
  
  // First check if there's a cookie issue flag and prevent unnecessary API calls
  // except for login, signup, and profile endpoints
  const hasCookieIssue = localStorage.getItem('cookie_auth_issue') === 'true';
  const isAuthEndpoint = endpoint.includes('/login') || 
                         endpoint.includes('/signup') || 
                         endpoint.includes('/auth') ||
                         endpoint.includes('/forgot-password') ||
                         endpoint.includes('/reset-password') ||
                         endpoint.includes('/verify');
  
  if (hasCookieIssue && !isAuthEndpoint) {
    console.warn('Cookie issue detected, using cached data if available');
    
    // Check if we have cached user data that we can use
    const userData = localStorage.getItem('user');
    if (userData && endpoint.includes('/profile')) {
      // For profile requests, return the cached data
      return { 
        error: false, 
        data: JSON.parse(userData),
        fromCache: true,
        status: 200 
      };
    }
    
    // Only show the warning once per session
    const hasShownWarning = sessionStorage.getItem('cookie-warning-shown');
    if (!hasShownWarning) {
      try {
        toast.warning(
          'Your browser is blocking authentication cookies. Please enable third-party cookies in your browser settings.',
          { 
            toastId: 'cookie-warning',
            autoClose: 7000
          }
        );
        sessionStorage.setItem('cookie-warning-shown', 'true');
      } catch (error) {
        console.error('Error showing toast notification:', error);
      }
    }
    
    // For non-profile requests with cookie issues, we should return an error
    if (!isAuthEndpoint) {
      return { 
        error: true, 
        message: 'Authentication cookies are blocked. Please enable third-party cookies in your browser settings.',
        status: 401,
        cookieIssue: true
      };
    }
  }
 
  try {
    // Check if localStorage has auth data we can use as fallback
    let authFromLocalStorage = null;
    let authToken = null;
    try {
      const userData = localStorage.getItem('user');
      const userId = localStorage.getItem('user_id');
      // Get the auth_token if stored (from login response)
      authToken = localStorage.getItem('auth_token');
      
      if (userData && userId) {
        authFromLocalStorage = {
          userData: JSON.parse(userData),
          userId
        };
      }
    } catch (authError) {
      console.warn("Could not get auth data from localStorage:", authError);
    }
    
    // Check for cookie access issues (improved for incognito mode)
    // In incognito mode, cookies might be restricted but localStorage usually works
    const cookiesAccessible = document.cookie.length > 0;
    let localStorageWorks = false;
    try {
      localStorage.setItem('_test', '1');
      localStorageWorks = localStorage.getItem('_test') === '1';
      localStorage.removeItem('_test');
    } catch (e) {
      // localStorage might be disabled
    }
    
    // Only consider it a cookie issue if cookies don't work AND localStorage doesn't work
    // This prevents false positives in incognito mode where localStorage works as fallback
    const cookieIssue = !cookiesAccessible && !localStorageWorks;
    
    // Set up headers for fetch request
    const baseHeaders = {
      'X-Requested-With': 'XMLHttpRequest',
      ...options.headers,
    };
    
    // Only set Content-Type for non-FormData requests
    if (!(options.body instanceof FormData)) {
      baseHeaders['Content-Type'] = 'application/json';
    }
    
    // Use centralized helper to add Authorization header
    const headers = getAuthHeaders(baseHeaders);
    
    // Fallback: try to use user ID if no token available (less secure, but better than nothing)
    if (!authToken && authFromLocalStorage) {
      headers['X-User-ID'] = authFromLocalStorage.userData.user_id || authFromLocalStorage.userId;
    }

    // Prepare fetch options
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout

    const fetchOptions = {
      ...options,
      credentials: 'include', // Always include credentials
      headers,
      signal: controller.signal
    };

    // Make the API call
    let response;
    try {
      response = await fetch(url, fetchOptions);
      clearTimeout(timeoutId);
    } catch (error) {
       clearTimeout(timeoutId);
       if (error.name === 'AbortError') {
         throw new Error('Request timed out after 15 seconds');
       }
       throw error;
    }

    // Handle 401 Unauthorized with potential localStorage fallback
    if (response.status === 401) {
      // Try to parse response data to check for specific error messages
      let responseData;
      try {
        responseData = await response.json();
      } catch (e) {
        responseData = { detail: "Authentication failed" };
      }
      
      // Check if this is specifically a cookie-related issue
      if (responseData.detail && 
          (responseData.detail.includes("cookie") || 
           responseData.detail.includes("session") || 
           responseData.detail === "Not authenticated")) {
        
        // Set both cookie issue flags for consistency
        localStorage.setItem('cookie_auth_issue', 'true');
        localStorage.setItem('cookie_issue', 'true');
        
        // Only show the warning once per session
        const hasShownWarning = sessionStorage.getItem('cookie-warning-shown');
        if (!hasShownWarning) {
          toast.warning(
            'Your browser is blocking authentication cookies. Please enable third-party cookies in your browser settings.',
            { 
              toastId: 'cookie-warning',
              autoClose: 7000
            }
          );
          sessionStorage.setItem('cookie-warning-shown', 'true');
        }
        
        // For profile requests with localStorage data, use that as fallback
        if (authFromLocalStorage && endpoint.includes('/profile')) {
          console.warn('Using localStorage auth due to API 401 response');
          return { 
            error: false, 
            data: authFromLocalStorage.userData,
            fromCache: true,
            status: 200
          };
        }
      } else if (authFromLocalStorage && cookieIssue) {
        // If we have localStorage auth and cookies are blocked, try to use that
        console.warn('Using localStorage auth due to API 401 response');
        
        // For profile GET requests, we can return the cached user data directly
        if (endpoint.includes('/profile') && options.method === 'GET') {
          return { 
            error: false, 
            data: authFromLocalStorage.userData,
            fromCache: true,
            status: 200
          };
        }
      }
      
      // Handle standard 401 cases
      // Clear user data on genuine unauthorized responses
      localStorage.removeItem('user');
      localStorage.removeItem('user_id');
      localStorage.removeItem('profileStats');
      localStorage.removeItem('user_profile_timestamp');
      
      // Show error message
      toast.info(responseData.detail || "Your session has expired. Please login again.", {
        toastId: 'session-expired',
      });
      
      return { error: true, data: null, status: 401, message: responseData.detail };
    }
    
    // Parse the response
    let data;
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      const text = await response.text();
      try {
        data = JSON.parse(text);
      } catch (e) {
        data = text;
      }
    }
    
    // Handle error responses
    if (!response.ok) {
      console.error('API Error:', data);
      
      // Create a standardized error object for handleApiError
      const errorObj = {
        status: response.status,
        data: data,
        message: data?.detail || data?.message || response.statusText || 'An error occurred'
      };
      
      // Handle specific error statuses that need immediate action
      if (response.status === 402) {
        // Subscription expired
        try {
          const user = JSON.parse(localStorage.getItem('user') || '{}');
          if (user) {
            user.tokens_left = 0;
            localStorage.setItem('user', JSON.stringify(user));
          }
        } catch (e) {
          console.error('Error updating user tokens:', e);
        }
        
        setTimeout(() => {
          window.location.href = '/subscription';
        }, 1000);
      } else if (response.status === 403 && data?.detail === "Admin access required") {
        // Admin access issues
        const userJson = localStorage.getItem('user');
        if (userJson) {
          try {
            const user = JSON.parse(userJson);
            if (user.user_type === 'admin') {
              localStorage.removeItem('user');
              toast.error('Permission mismatch detected. Please login again to refresh your privileges.');
              
              setTimeout(() => {
                window.location.href = '/';
              }, 2000);
            }
          } catch (e) {
            console.error('Error parsing user data:', e);
          }
        }
      } else if (response.status === 500) {

        // Handle 500 errors with proper error message
        const serverErrorMessage = data?.detail || data?.message || 'Internal server error. Please try again later.';
        toast.error(serverErrorMessage);
      }
      
      return { error: true, data, status: response.status, message: errorObj.message };
    }
    
    // Clear cookie issue flag on successful responses
    if (hasCookieIssue && !endpoint.includes('/login') && !endpoint.includes('/signup')) {
      localStorage.removeItem('cookie_auth_issue');
    }
    
    return { error: false, data, status: response.status };
  } catch (error) {
    console.error('Fetch error:', error);
    
    // Create a standardized error object for handleApiError
    const standardizedError = {
      status: error.status || error.code || 500,
      data: error.data || error,
      message: error.message || errorMessage || 'Network error occurred'
    };
    
    return handleApiError(standardizedError, errorMessage);
  }
};

export const handleApiError = (error, customMessage = "An error occurred") => {

  // Safely extract status and data from error object
  let status = null;
  let errorData = null;
  let errorMessage = customMessage;
  
  // Handle different error object structures
  if (error) {
    // Check for Axios error structure first (most common)
    if (error.response && error.response.status) {
      status = error.response.status;
      errorData = error.response.data || error.response;
      // For Axios errors, also include statusText from response
      const statusText = error.response.statusText || '';
      errorMessage = errorData.detail || errorData.message || statusText || error.message || customMessage;
    }
    // Check for direct status property
    else if (error.status) {
      status = error.status;
      errorData = error.data || error;
      errorMessage = errorData.detail || errorData.message || errorData.statusText || error.message || customMessage;
    }
    // Check for nested data.status property
    else if (error.data && error.data.status) {
      status = error.data.status;
      errorData = error.data;
      errorMessage = errorData.detail || errorData.message || errorData.statusText || customMessage;
    }
    // Check for Axios error without response (network errors)
    else if (error.code && error.name === 'AxiosError') {
      // Handle Axios network errors
      status = 500; // Default to 500 for network errors
      errorData = error;
      errorMessage = error.message || 'Network error occurred';
    }
    // Fallback: try to extract from error object itself
    else if (error.code || error.statusCode) {
      status = error.code || error.statusCode;
      errorData = error;
      errorMessage = error.message || customMessage;
    }
    // Final fallback for error objects with just a message
    else if (error.message) {
      errorMessage = error.message;
      status = 500; // Default status for unknown errors
    }
  }
  
  // Handle specific status codes
  if (status) {
    switch (status) {
      case 400:
        toast.error(errorMessage || "Bad Request");
        return { error: true, message: errorMessage || "Bad Request", status };
        
      case 401:
        // Check for specific session-related error messages
        if (errorMessage && errorMessage.toLowerCase().includes("cookies")) {
          localStorage.setItem('cookie_auth_issue', 'true');
          localStorage.removeItem('user');
          localStorage.removeItem('user_id');
          localStorage.removeItem('profileStats');
          localStorage.removeItem('user_profile_timestamp');
          return { error: true, message: errorMessage || "Your session has expired. Please login again.", status };
        } else if (errorMessage && errorMessage.toLowerCase().includes("another device")) {
          // Multiple device login detected
          localStorage.removeItem('user');
          localStorage.removeItem('user_id');
          localStorage.removeItem('profileStats');
          localStorage.removeItem('user_profile_timestamp');
          
          // Show specific toast for multiple device login
          toast.warning("🔒 Multiple Device Login Detected", {
            toastId: 'multiple-device-login',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            render: () => (
              <div>
                <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                  🔒 Multiple Device Login Detected
                </div>
                <div style={{ fontSize: '14px', lineHeight: '1.4' }}>
                  Your account is being used on another device. For security reasons, you have been logged out from this device.
                </div>
                <div style={{ fontSize: '12px', marginTop: '8px', color: '#888' }}>
                  Please ensure you're only logged in on one device at a time.
                </div>
              </div>
            )
          });
          
          return { error: true, message: errorMessage, status };
        } else if (errorMessage && errorMessage.toLowerCase().includes("invalidated")) {
          // Session invalidated
          localStorage.removeItem('user');
          localStorage.removeItem('user_id');
          localStorage.removeItem('profileStats');
          localStorage.removeItem('user_profile_timestamp');
          
          toast.info("Your session has been invalidated. Please login again.", {
            toastId: 'session-invalidated',
            autoClose: 4000
          });
          
          return { error: true, message: errorMessage, status };
        } else if (errorMessage && errorMessage.toLowerCase().includes("refresh")) {
          // Session needs refresh
          localStorage.removeItem('user');
          localStorage.removeItem('user_id');
          localStorage.removeItem('profileStats');
          localStorage.removeItem('user_profile_timestamp');
          
          toast.warning("Please refresh your browser or clear your browser data. Your session needs to be updated.", {
            toastId: 'session-refresh-needed',
            autoClose: 6000
          });
          
          return { error: true, message: errorMessage, status };
        } else {
          // Standard session expiry
          localStorage.removeItem('user');
          localStorage.removeItem('user_id');
          localStorage.removeItem('profileStats');
          localStorage.removeItem('user_profile_timestamp');
          
          // Show error message
          toast.info(errorMessage || "Your session has expired. Please login again.", {
            toastId: 'session-expired',
          });
          
          return { error: true, message: errorMessage || "Session expired", status };
        }
        
      case 402:
        const subscriptionMessage = errorMessage || "Subscription expired. Please renew your subscription.";
        toast.error(subscriptionMessage);
        
        try {
          const user = JSON.parse(localStorage.getItem('user') || '{}');
          if (user) {
            user.tokens_left = 0;
            localStorage.setItem('user', JSON.stringify(user));
          }
        } catch (e) {
          console.error('Error updating user tokens:', e);
        }
        
        setTimeout(() => {
          window.location.href = '/subscription';
        }, 1000);
        
        return { error: true, message: subscriptionMessage, status };
        
      case 403:
        const permissionMessage = errorMessage || "You don't have permission to access this resource.";
        
        // Check if this is related to admin access
        if (errorMessage === "Admin access required") {
          const userJson = localStorage.getItem('user');
          if (userJson) {
            try {
              const user = JSON.parse(userJson);
              
              if (user.user_type === 'admin') {
                toast.error("Admin verification failed. Your session may have expired. Please login again.");
                
                // Clear user data to force re-login
                localStorage.removeItem('user');
                localStorage.removeItem('user_id');
                
                // Redirect after a delay
                setTimeout(() => {
                  window.location.href = '/';
                }, 2000);
              }
            } catch (e) {
              console.error('Error parsing user data during 403 error:', e);
            }
          }
        }
        
        toast.error(permissionMessage);
        return { error: true, message: permissionMessage, status };
        
      case 404:
        const notFoundMessage = errorMessage || "Resource not found.";
        toast.error(notFoundMessage);
        return { error: true, message: notFoundMessage, status };
        
      case 429:
        const rateLimitMessage = errorMessage || "Too many requests. Please try again later.";
        toast.error(rateLimitMessage);
        return { error: true, message: rateLimitMessage, status };
        
      case 500:
        const serverErrorMessage = errorMessage || "Internal server error. Please try again later.";
        toast.error(serverErrorMessage);
        return { error: true, message: serverErrorMessage, status, details: errorData };
        
             default:
         // Other response error statuses
         toast.error(errorMessage);
         return { error: true, message: errorMessage, status };
     }
   }
   
   // Handle cases where no status was found but we still have an error
   if (error) {
     // Check for network errors
     if (error.message && (
       error.message.includes('Network Error') ||
       error.message.includes('fetch') ||
       error.message.includes('Failed to fetch')
     )) {
       const networkMessage = "Network error. Please check your internet connection and try again.";
       toast.error(networkMessage);
       return { error: true, message: networkMessage };
     }
     
     // Check if this is likely a cookie issue
     if (document.cookie.length === 0) {
       const cookieMessage = "Your browser may be blocking cookies. Please check your privacy settings.";
       toast.error(cookieMessage);
       return { error: true, message: cookieMessage };
     }
     
     // Generic error fallback
     const fallbackMessage = errorMessage || error.message || "An unexpected error occurred";
     toast.error(fallbackMessage);
     return { error: true, message: fallbackMessage };
       }
    
    // Final fallback for any unhandled cases
    const finalMessage = customMessage || "An unexpected error occurred";
    toast.error(finalMessage);
    return { error: true, message: finalMessage };
};

// Helper function to detect cookie issues
export const detectCookieIssues = () => {
  try {
    // Simple cookie test - just check if we can set and read a basic cookie
    const testValue = `test-${Date.now()}`;
    
    // Set a simple test cookie with minimal flags
    document.cookie = `cookieTest=${testValue}; path=/; max-age=60`;
    
    // Try to read it back
    const hasCookie = document.cookie.indexOf(`cookieTest=${testValue}`) !== -1;
    
    // Clean up the test cookie
    document.cookie = `cookieTest=${testValue}; path=/; max-age=0`;
    
    return {
      hasIssue: !hasCookie,
      cookiesAccessible: hasCookie
    };
  } catch (error) {
    console.error('Error detecting cookie issues:', error);
    return {
      hasIssue: true,
      cookiesAccessible: false
    };
  }
};

// Helper to ensure cookie access before API calls
export const ensureCookieAccess = async () => {
  try {
    // Check if cookie issues have already been detected
    const hasCookieIssue = localStorage.getItem('cookie_auth_issue') === 'true' || 
                           localStorage.getItem('cookie_issue') === 'true';
    if (hasCookieIssue) {
      // We already know there's an issue, no need to try again
      console.warn("Cookie access issue previously detected, skipping cookie check");
      return false;
    }
    
    // Check if user is logged in first
    const user = localStorage.getItem('user');
    const userId = localStorage.getItem('user_id');
    
    // If no user data in localStorage, we're not logged in, so no need to check cookies
    if (!user || !userId) {
      return false;
    }
    
    // Check if cookies are already accessible
    if (document.cookie.length > 0) {
      return true; // Cookies already accessible, no need for API call
    }
    
    // Only ping API if user is logged in but cookies aren't accessible yet
    try {
      const response = await fetch(`${API_URL}/ping`, {
        method: 'GET',
        credentials: 'include'
      });
      
      if (!response.ok) {
        // If we got a non-OK response, check for 401 Unauthorized
        if (response.status === 401) {
          // Set both flags for consistency across the app
          localStorage.setItem('cookie_auth_issue', 'true');
          localStorage.setItem('cookie_issue', 'true');
          
          // We now handle this with our persistent banner, so we don't need the toast here
          return false;
        }
      }
      
      return response.ok;
    } catch (fetchError) {
      console.warn("Error pinging API:", fetchError);
      // If we can't connect, set both cookie issue flags
      localStorage.setItem('cookie_auth_issue', 'true');
      localStorage.setItem('cookie_issue', 'true');
      return false;
    }
  } catch (error) {
    console.warn("Error ensuring cookie access:", error);
    // Set both cookie issue flags on general errors too
    localStorage.setItem('cookie_auth_issue', 'true');
    localStorage.setItem('cookie_issue', 'true');
    return false;
  }
};

// Get authentication status helper function
export const getAuthStatus = async () => {
  try {
    // Try to get cached user data from localStorage
    const userJson = localStorage.getItem('user');
    const userId = localStorage.getItem('user_id');
    
    if (userJson && userId) {
      const userData = JSON.parse(userJson);
      
      // Check if the timestamp is relatively recent
      const timestamp = localStorage.getItem('user_profile_timestamp');
      const isRecent = timestamp && (Date.now() - parseInt(timestamp) < 24 * 60 * 60 * 1000); // Within 24 hours
      
      if (userData && userData.user_id && isRecent) {
        return {
          isAuthenticated: true,
          userData,
          fromLocalStorage: true
        };
      }
    }
    
    // If cookies are accessible, check with the server
    // if (document.cookie.length > 0) {
    //   try {
    //     const response = await fetch(`${API_URL}/check-auth`, {
    //       method: 'GET',
    //       credentials: 'include'
    //     });
        
    //     if (response.ok) {
    //       const data = await response.json();
    //       if (data.authenticated) {
    //         return {
    //           isAuthenticated: true,
    //           userData: data.user,
    //           fromServer: true
    //         };
    //       }
    //     }
    //   } catch (e) {
    //     console.warn("Error checking auth with server:", e);
    //   }
    // }
    
    return { isAuthenticated: false };
  } catch (error) {
    console.error("Error getting auth status:", error);
    return { isAuthenticated: false, error };
  }
};

 